import { ArrowUpRight, Minus, Plus, X } from 'lucide-react';
import { useEffect, useRef, useState } from 'react';

import type { PharmacyInfo } from '@/entities/business-relationship/api/businessRelationship.api';
import { AppOnlyButton } from '@/shared/components/app-guide/AppOnlyButton';
import { EmptyState } from '@/shared/components/StateViews';
import { Button } from '@/shared/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogTitle,
} from '@/shared/components/ui/dialog';
import { cn } from '@/shared/lib/utils';

import { copyWithToast } from '../utils';

/**
 * M-21 거래처 헤더 오버레이 — 주소 모달(520) · 사업자등록증 모달(720) · 문전약국 팝업(640).
 * 셋 다 조회 전용이고 ✕ · ESC · 배경 클릭으로 닫힌다. 등록 · 수정은 앱(X6).
 */

const ModalHeader = ({ title }: { title: string }) => (
  <div className="flex shrink-0 items-center border-b border-line py-[18px] pr-14 pl-6">
    <DialogTitle className="text-base leading-6 font-bold">{title}</DialogTitle>
  </div>
);

const mapLinks = (address: string) => [
  {
    label: '네이버 지도에서 열기',
    href: `https://map.naver.com/p/search/${encodeURIComponent(address)}`,
  },
  {
    label: '카카오 지도에서 열기',
    href: `https://map.kakao.com/?q=${encodeURIComponent(address)}`,
  },
];

/** 주소 모달 (520) — 지도는 주소를 검색어로 넘겨 새 탭에서 연다. 앱의 T map은 뺐다 */
export const AddressDialog = ({
  open,
  onOpenChange,
  clinicName,
  address,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  clinicName: string;
  address: string;
}) => (
  <Dialog open={open} onOpenChange={onOpenChange}>
    <DialogContent className="lg:max-w-[520px]">
      <ModalHeader title="주소" />
      <DialogDescription className="sr-only">
        {clinicName} 주소와 지도 바로가기
      </DialogDescription>
      <div className="flex flex-col gap-2 border-b border-line px-6 py-5">
        <p className="text-sm leading-[21px] font-bold text-ink">
          {clinicName}
        </p>
        <div className="flex items-center gap-3">
          <p className="min-w-0 flex-1 text-[13px] leading-5 break-keep text-ink-2">
            {address}
          </p>
          <Button
            variant="outline"
            size="md"
            className="rounded-[9px]"
            onClick={() =>
              void copyWithToast(
                address,
                '주소가 복사되었어요.',
                '주소를 복사하지 못했어요.',
              )
            }
          >
            주소 복사
          </Button>
        </div>
      </div>
      <div className="flex flex-col gap-2 px-6 py-4">
        {mapLinks(address).map((l) => (
          <a
            key={l.label}
            href={l.href}
            target="_blank"
            rel="noopener noreferrer"
            className="flex h-[42px] items-center gap-1.5 rounded-md bg-field px-3.5 text-[13px] leading-5 font-medium text-ink-2 hover:bg-muted"
          >
            {l.label}
            <ArrowUpRight className="size-4" aria-hidden />
          </a>
        ))}
      </div>
    </DialogContent>
  </Dialog>
);

const ZOOM_STEPS = [25, 50, 75, 100, 125, 150, 200, 300, 400];
const ZOOM_MIN = ZOOM_STEPS[0];
const ZOOM_MAX = ZOOM_STEPS[ZOOM_STEPS.length - 1];

/**
 * 사업자등록증 모달 (720) — 열람만 제공한다. 재등록은 앱(A2 비활성 + X6).
 * 확대 · 축소는 25~400%. 처음과 [맞춤]은 뷰어 영역에 꽉 맞춰(작은 이미지도 키워서) 보여주고, [원본 크기]는 100%.
 * PDF도 #view=Fit으로 한 페이지가 뷰어에 맞게 보이도록 연다.
 * 이미지는 presigned URL(10분 만료)이라 실패하면 상세를 한 번 자동으로 다시 받아 새 URL로 재시도한다.
 * 화면을 연 지 10분이 지나 URL이 만료된 경우가 가장 흔하다. 재시도도 실패하면 오류 화면을 보여 준다.
 * 앱에서 PDF로 올린 등록증도 있어 PDF는 <img> 대신 브라우저 PDF 뷰어(iframe)로 보여 준다.
 */
const isPdfUrl = (url: string) => {
  try {
    return new URL(url).pathname.toLowerCase().endsWith('.pdf');
  } catch {
    return url.split('?')[0].toLowerCase().endsWith('.pdf');
  }
};

export const BusinessLicenseDialog = ({
  open,
  onOpenChange,
  clinicName,
  imageUrl,
  onReload,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  clinicName: string;
  imageUrl: string;
  onReload: () => void;
}) => {
  const viewportRef = useRef<HTMLDivElement>(null);
  const [natural, setNatural] = useState<{ w: number; h: number } | null>(null);
  /** 맞춤 상태의 실제 배율(%) — 확대 · 축소의 출발점. 이미지가 뜰 때 잰다 */
  const [fit, setFit] = useState(100);
  /** null = 맞춤 */
  const [zoom, setZoom] = useState<number | null>(null);
  const [failed, setFailed] = useState(false);
  /** 만료 대비 자동 재시도는 팝업을 열 때마다 한 번만 한다 */
  const [retried, setRetried] = useState(false);
  const pdf = isPdfUrl(imageUrl);

  useEffect(() => {
    if (!open) {
      setZoom(null);
      setRetried(false);
    }
  }, [open]);

  useEffect(() => {
    setFailed(false);
    setNatural(null);
  }, [imageUrl]);

  const handleImageError = () => {
    if (!retried) {
      setRetried(true);
      onReload(); // 새 presigned URL을 받으면 imageUrl이 바뀌어 다시 그린다
      return;
    }
    setFailed(true);
  };

  const currentPercent = zoom ?? fit;

  const measureFit = (w: number, h: number) => {
    const el = viewportRef.current;
    if (!el || !w || !h) return;
    // 작은 이미지도 뷰어 크기에 맞춰 키운다 (상한 없음)
    setFit(Math.min(el.clientWidth / w, el.clientHeight / h) * 100);
  };

  const zoomIn = () => {
    const next = ZOOM_STEPS.find((s) => s > currentPercent + 0.5) ?? ZOOM_MAX;
    setZoom(next);
  };
  const zoomOut = () => {
    const prev =
      [...ZOOM_STEPS].reverse().find((s) => s < currentPercent - 0.5) ??
      ZOOM_MIN;
    setZoom(prev);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="lg:max-h-[94vh] lg:max-w-[720px]">
        <ModalHeader title="사업자등록증" />
        <DialogDescription className="sr-only">
          {clinicName} 사업자등록증 이미지
        </DialogDescription>
        <div className="flex min-h-0 flex-1 flex-col gap-3 px-6 py-5">
          <p className="text-sm leading-[21px] font-bold text-ink">
            {clinicName}
          </p>
          <div
            ref={viewportRef}
            className={cn(
              'relative h-[62dvh] min-h-[320px] overflow-auto rounded-lg border border-line bg-field lg:h-[min(72vh,780px)]',
              zoom === null && 'flex items-center justify-center',
            )}
          >
            {failed ? (
              <EmptyState
                className="h-full"
                message="이미지를 불러오지 못했어요."
                action={
                  <Button variant="secondary" size="sm" onClick={onReload}>
                    다시 불러오기
                  </Button>
                }
              />
            ) : pdf ? (
              <iframe
                // 브라우저 PDF 뷰어에 "페이지 전체 맞춤"으로 열라고 알려 준다 (fragment라 서명에 영향 없음)
                src={`${imageUrl}#view=Fit`}
                title={`${clinicName} 사업자등록증 (PDF)`}
                className="h-full w-full bg-surface"
              />
            ) : (
              <img
                src={imageUrl}
                alt={`${clinicName} 사업자등록증`}
                draggable={false}
                onLoad={(e) => {
                  const { naturalWidth: w, naturalHeight: h } = e.currentTarget;
                  setNatural({ w, h });
                  measureFit(w, h);
                }}
                onError={handleImageError}
                className={cn(
                  'select-none',
                  zoom === null ? 'h-full w-full object-contain' : 'max-w-none',
                )}
                style={
                  zoom !== null && natural
                    ? { width: (natural.w * zoom) / 100 }
                    : undefined
                }
              />
            )}
          </div>
          <div className="flex flex-wrap items-center gap-2">
            <Button
              variant="outline"
              size="md"
              className="rounded-[9px]"
              disabled={failed || pdf || currentPercent >= ZOOM_MAX}
              onClick={zoomIn}
            >
              <Plus aria-hidden />
              확대
            </Button>
            <Button
              variant="outline"
              size="md"
              className="rounded-[9px]"
              disabled={failed || pdf || currentPercent <= ZOOM_MIN}
              onClick={zoomOut}
            >
              <Minus aria-hidden />
              축소
            </Button>
            <Button
              variant="outline"
              size="md"
              className="rounded-[9px]"
              disabled={failed || pdf}
              aria-pressed={zoom === null}
              onClick={() => {
                setZoom(null);
                if (natural) measureFit(natural.w, natural.h);
              }}
            >
              맞춤
            </Button>
            <Button
              variant="outline"
              size="md"
              className="rounded-[9px]"
              disabled={failed || pdf}
              aria-pressed={zoom === 100}
              onClick={() => setZoom(100)}
            >
              원본 크기
            </Button>
            {pdf ? (
              <a
                href={imageUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="text-xs leading-[18px] font-medium text-primary hover:underline"
              >
                새 탭에서 열기
              </a>
            ) : (
              <span className="text-xs leading-[18px] text-ink-6 tabular-nums">
                {Math.round(currentPercent)}%
              </span>
            )}
            <AppOnlyButton
              feature="businessLicenseEdit"
              size="lg"
              className="ml-auto"
            >
              사업자등록증 수정
            </AppOnlyButton>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

/**
 * 문전약국 팝업 (640) — 조회만. 등록 · 해제와 「최소 1곳 유지」 규칙은 앱에서 적용한다.
 * 폐업 · 삭제로 약국 정보가 없으면 「정보 없음」으로 표시한다.
 * @see Figma M-21 드로어/문전약국 365:31 — 이름은 드로어지만 중앙 팝업 형태다
 */
export const PharmacyDialog = ({
  open,
  onOpenChange,
  pharmacies,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  pharmacies: PharmacyInfo[];
}) => (
  <Dialog open={open} onOpenChange={onOpenChange}>
    <DialogContent
      hideClose
      className="lg:max-w-[640px] lg:rounded-[14px] lg:shadow-[0_8px_28px_rgb(0_0_0/10%)]"
    >
      <header className="flex shrink-0 items-center gap-2.5 border-b border-[#efefef] px-6 pt-5 pb-4">
        <DialogTitle className="text-base leading-6 font-bold text-ink">
          문전약국 {pharmacies.length}곳
        </DialogTitle>
        <button
          type="button"
          onClick={() => onOpenChange(false)}
          aria-label="닫기"
          className="ml-auto rounded-sm p-0.5 text-ink-4 outline-none hover:text-ink focus-visible:ring-2 focus-visible:ring-primary/40"
        >
          <X className="size-[18px]" />
        </button>
      </header>
      <DialogDescription className="sr-only">
        연결된 문전약국 목록
      </DialogDescription>
      <div className="min-h-0 flex-1 overflow-y-auto">
        {pharmacies.length === 0 ? (
          <EmptyState className="py-16" message="연결된 문전약국이 없어요." />
        ) : (
          <ul>
            {pharmacies.map((p) => {
              const unknown = !!p.closedOn;
              return (
                <li
                  key={p.pharmacyId}
                  className="flex flex-col gap-1 border-b border-[#f4f4f4] px-6 py-3"
                >
                  <div className="flex items-center gap-2">
                    <p
                      className={cn(
                        'min-w-0 flex-1 text-[13.5px] leading-[21px] font-medium',
                        unknown ? 'text-ink-6' : 'text-ink',
                      )}
                    >
                      {p.pharmacyName}
                    </p>
                  </div>
                  <p className="text-[11.5px] leading-[18px] text-[#8a8a8a]">
                    {unknown || !p.address ? '정보 없음' : p.address}
                  </p>
                </li>
              );
            })}
          </ul>
        )}
      </div>
      <footer className="shrink-0 px-6 pt-4 pb-5">
        <AppOnlyButton
          feature="frontPharmacy"
          className="h-auto w-full py-[9px] text-[12.5px] leading-[19px]"
        >
          문전약국 설정
        </AppOnlyButton>
      </footer>
    </DialogContent>
  </Dialog>
);
